import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, {ReactNode, useEffect, useState} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import AbsoluteDropdownContainer from 'ui-library/AbsoluteDropdownContainer';
import {GRAY_LIGHTER, NEAR_BLACK} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

export interface OptionCellDropdownOption {
  node: ReactNode | string;
  onUpdateKey: string;
  disabled?: boolean;
}

/**
 * Pick from a list of options in a cell value. Unlike SelectorDropdown, this isn't tied
 * with the ELN selector system, and instead simply shows any array of options.
 */
export interface OptionCellDropdownProps {
  isSelected: boolean;
  locked: boolean;
  // If specified, menuHeader and menuFooter should be <li> tags, or arrays of them, which will go
  // at the top and bottom of the menu.
  menuHeader?: ReactNode;
  menuFooter?: ReactNode;
  options: Array<string | OptionCellDropdownOption>;
  onOpenDropdown?: (() => void) | null;
  onUpdate: (selectedOption: string) => void;
  staticDisplay?: string | ReactNode;
  fullWidth?: boolean;
  lightHoverBackground?: boolean;
}

export default function OptionCellDropdown(props: OptionCellDropdownProps): JSX.Element {
  const [isOpen, setOpen] = useState(false);

  useEffect(() => {
    if (!props.isSelected) {
      setOpen(false);
    }
  }, [props.isSelected]);

  function _handleOpenMenu(): void {
    props.onOpenDropdown?.(); // Select the cell when its menu is opened to re-inforce editing the cell
    setOpen(true);
  }

  let fullWidthClickHandler;
  if (props.fullWidth) {
    fullWidthClickHandler = _handleOpenMenu;
  }

  return (
    <div className="u-pointer u-flex-display" onClick={fullWidthClickHandler}>
      <span className={classNames('u-flex-grow', css(styles.displayContents))}>
        {props.staticDisplay || (props.fullWidth ? '\u00A0' : '')}
      </span>
      <div className="selectorDropdown">
        <AbsoluteDropdownContainer
          onRequestClose={() => {
            setOpen(false);
          }}
          open={isOpen}
          shouldCloseOnInsideClick={true}
          menu={
            <ul className="selectorDropdown-list dropdown-menu scrollable pull-right">
              {props.menuHeader}
              {props.options.map((option, i) => {
                const disabled = typeof option === 'object' && option.disabled;
                return (
                  <li key={`option-${i}`} className={classNames({disabled: props.locked || disabled})}>
                    <a
                      className={props.lightHoverBackground ? css(styles.dropdownOption) : ''}
                      onClick={() => {
                        if (!props.locked && !disabled) {
                          props.onUpdate(typeof option === 'string' ? option : option.onUpdateKey);
                        }
                      }}
                      {...getTestProps({component: 'OptionCellDropdown', element: 'anchor'})}
                    >
                      {typeof option === 'string' ? option : option.node}
                    </a>
                  </li>
                );
              })}
              {props.menuFooter}
            </ul>
          }
        >
          <BenchlingIcon
            className={classNames('selectorDropdown-button', {'is-open': isOpen})}
            icon="chevron-down"
            onClick={_handleOpenMenu}
            {...getTestProps({component: 'OptionCellDropdown', element: 'toggle'})}
          />
        </AbsoluteDropdownContainer>
      </div>
    </div>
  );
}

OptionCellDropdown.propTypes = {
  isSelected: PropTypes.bool.isRequired,
  locked: PropTypes.bool.isRequired,
  // If specified, menuHeader and menuFooter should be <li> tags, or arrays of them, which will go
  // at the top and bottom of the menu.
  menuHeader: PropTypes.node,
  menuFooter: PropTypes.node,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string.isRequired,
      PropTypes.shape({
        node: PropTypes.node.isRequired,
        onUpdateKey: PropTypes.string.isRequired,
      }),
    ])
  ).isRequired,
  onOpenDropdown: PropTypes.func,
  onUpdate: PropTypes.func.isRequired,
};

export const styles = StyleSheet.create({
  dropdownOption: {
    ':hover': {
      backgroundColor: GRAY_LIGHTER,
      color: NEAR_BLACK,
      textDecoration: 'none',
    },
  },
  displayContents: {
    overflow: 'hidden',
    marginRight: 14,
  },
});
